import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

export interface IntuiAccordionItem {
  /** Unique value for this item */
  value: string
  /** Trigger text or element */
  trigger: ReactNode
  /** Content revealed when expanded */
  content: ReactNode
  /** Disable this item */
  disabled?: boolean
}

export interface IntuiAccordionProps {
  /** Accordion items to render */
  items: IntuiAccordionItem[]
  /** Allow multiple items open at once */
  multiple?: boolean
  /** Controlled open value(s) */
  value?: string | string[]
  /** Default open value(s) */
  defaultValue?: string | string[]
  /** Callback when value changes */
  onValueChange?: (value: string | string[]) => void
  /** Additional className */
  className?: string
  /** Collapsible — allow all items to be closed (single mode only) */
  collapsible?: boolean
}

/**
 * IntuiAccordion — Caliper Design System
 *
 * Figma: border-b per item, py-4 trigger, text-sm content,
 * ChevronDown icon rotates on open, hover:underline on trigger.
 * Wraps @/components/ui/accordion (Radix).
 */
function IntuiAccordion({
  items,
  multiple = false,
  value,
  defaultValue,
  onValueChange,
  className,
  collapsible = true,
}: IntuiAccordionProps) {
  if (multiple) {
    return (
      <Accordion
        type="multiple"
        value={value as string[] | undefined}
        defaultValue={defaultValue as string[] | undefined}
        onValueChange={onValueChange as ((value: string[]) => void) | undefined}
        className={cn('w-full', className)}
      >
        {items.map((item) => (
          <AccordionItem key={item.value} value={item.value} disabled={item.disabled}>
            <AccordionTrigger className="text-sm font-medium">
              {item.trigger}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              {item.content}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    )
  }

  return (
    <Accordion
      type="single"
      collapsible={collapsible}
      value={value as string | undefined}
      defaultValue={defaultValue as string | undefined}
      onValueChange={onValueChange as ((value: string) => void) | undefined}
      className={cn('w-full', className)}
    >
      {items.map((item) => (
        <AccordionItem key={item.value} value={item.value} disabled={item.disabled}>
          {/* Figma (22:371): py-3 (12px), text-sm font-medium leading-5 text-foreground, ChevronDown 16px */}
          <AccordionTrigger className="py-3 text-sm font-medium leading-5">
            {item.trigger}
          </AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground">
            {item.content}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

export { IntuiAccordion }
